import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface QualificationData {
  // Chat intent
  chatChannels: string[];
  chatVolume: string | null;
  // Selling intent
  sellingCategory: string | null;
  sellingChannels: string[];
  // POS intent
  posType: string | null;
  posBranches: string | null;
  // CRM intent
  crmTeamSize: string | null;
  crmCurrentTool: string | null;
}

export interface RegistrationFormData {
  // Step 0: About You
  firstName: string;
  lastName: string;

  // Step 1: Contact
  email: string;
  password: string;

  // Step 2: Company
  companyName: string;

  // Step 3: Phone (mandatory for LINE mode with OTP)
  phoneNumber: string;

  // Step 4: Business Type (optional)
  businessType: string | null;

  // Step 5: App Interests (optional)
  appInterests: string[];

  // Desktop Handoff: real email for Google Ads ECL
  realEmail: string;
}

const initialQualificationData: QualificationData = {
  chatChannels: [],
  chatVolume: null,
  sellingCategory: null,
  sellingChannels: [],
  posType: null,
  posBranches: null,
  crmTeamSize: null,
  crmCurrentTool: null,
};

interface RegistrationState {
  currentStep: number;
  formData: RegistrationFormData;
  isSubmitting: boolean;
  error: string | null;
  hasHydrated: boolean;

  // LP intent (e.g. 'selling', 'chat', 'contacts')
  intent: string | null;

  // Intent-specific qualification answers
  qualificationData: QualificationData;

  // Google mode fields
  isGoogleMode: boolean;
  googleCredential: string | null;

  // LINE mode fields
  isLineMode: boolean;
  lineUserId: string | null;
  linePictureUrl: string | null;

  // OTP verification state
  phoneVerified: boolean;
  otpRefCode: string | null;
  otpSentAt: number | null;  // Unix timestamp for cooldown timer
  otpStep: 'phone' | 'pin'; // Sub-step within Step 3

  // Actions
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateFormData: (data: Partial<RegistrationFormData>) => void;
  updateQualificationData: (data: Partial<QualificationData>) => void;
  setError: (error: string | null) => void;
  setSubmitting: (isSubmitting: boolean) => void;
  reset: () => void;

  // Intent action
  setIntent: (intent: string | null) => void;

  // Google mode actions
  setGoogleMode: (credential: string) => void;
  clearGoogleMode: () => void;

  // LINE mode actions
  setLineMode: (lineUserId: string, firstName: string, lastName: string, pictureUrl: string) => void;
  clearLineMode: () => void;

  // OTP actions
  setOTPSent: (refCode: string) => void;
  setPhoneVerified: () => void;
  resetOTP: () => void;
}

const initialFormData: RegistrationFormData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  companyName: '',
  phoneNumber: '',
  businessType: null,
  appInterests: [],
  realEmail: '',
};

export const useRegistrationStore = create<RegistrationState>()(
  persist(
    (set, get) => ({
      currentStep: 0,
      formData: initialFormData,
      isSubmitting: false,
      error: null,
      hasHydrated: false,

      // Intent initial state
      intent: null,
      qualificationData: { ...initialQualificationData },

      // Google mode initial state
      isGoogleMode: false,
      googleCredential: null,

      // LINE mode initial state
      isLineMode: false,
      lineUserId: null,
      linePictureUrl: null,

      // OTP initial state
      phoneVerified: false,
      otpRefCode: null,
      otpSentAt: null,
      otpStep: 'phone' as const,

      setStep: (step) => set({ currentStep: step, error: null }),
      nextStep: () =>
        set((state) => ({
          currentStep: state.currentStep + 1,
          error: null,
        })),
      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0),
          error: null,
        })),
      updateFormData: (data) =>
        set((state) => ({
          formData: { ...state.formData, ...data },
        })),
      updateQualificationData: (data) =>
        set((state) => ({
          qualificationData: { ...state.qualificationData, ...data },
        })),
      setError: (error) => set({ error }),
      setSubmitting: (isSubmitting) => set({ isSubmitting }),
      reset: () =>
        set({
          currentStep: 0,
          formData: initialFormData,
          isSubmitting: false,
          error: null,
          intent: null,
          qualificationData: { ...initialQualificationData },
          // Reset Google mode
          isGoogleMode: false,
          googleCredential: null,
          // Also reset OTP state
          phoneVerified: false,
          otpRefCode: null,
          otpSentAt: null,
          otpStep: 'phone' as const,
        }),

      // Intent action
      setIntent: (intent) => set({ intent }),

      // Google mode actions
      setGoogleMode: (credential) =>
        set({
          isGoogleMode: true,
          googleCredential: credential,
          currentStep: 1, // Skip auth_method step, go to company
        }),

      clearGoogleMode: () =>
        set({
          isGoogleMode: false,
          googleCredential: null,
          currentStep: 0,
        }),

      // LINE mode actions
      setLineMode: (lineUserId, firstName, lastName, pictureUrl) =>
        set({
          isLineMode: true,
          lineUserId,
          linePictureUrl: pictureUrl || null,
          currentStep: 2, // Skip to company step
          formData: {
            ...get().formData,
            firstName,
            lastName,
          },
        }),

      clearLineMode: () =>
        set({
          isLineMode: false,
          lineUserId: null,
          linePictureUrl: null,
          intent: null,
          currentStep: 0, // Reset step when clearing LINE mode
          qualificationData: { ...initialQualificationData },
          // Also reset OTP state
          phoneVerified: false,
          otpRefCode: null,
          otpSentAt: null,
          otpStep: 'phone' as const,
        }),

      // OTP actions
      setOTPSent: (refCode) =>
        set({
          otpRefCode: refCode,
          otpSentAt: Date.now(),
          otpStep: 'pin' as const,
        }),
      setPhoneVerified: () =>
        set({
          phoneVerified: true,
          otpStep: 'phone' as const, // Reset sub-step (done)
        }),
      resetOTP: () =>
        set({
          phoneVerified: false,
          otpRefCode: null,
          otpSentAt: null,
          otpStep: 'phone' as const,
        }),
    }),
    {
      name: 'registration-storage',
      skipHydration: true, // Manual hydration like authStore
      partialize: (state) => ({
        currentStep: state.currentStep,
        intent: state.intent,
        qualificationData: state.qualificationData,
        isGoogleMode: state.isGoogleMode,
        googleCredential: state.googleCredential,
        isLineMode: state.isLineMode,
        lineUserId: state.lineUserId,
        linePictureUrl: state.linePictureUrl,
        // OTP state (persisted for refresh recovery)
        phoneVerified: state.phoneVerified,
        otpRefCode: state.otpRefCode,
        otpSentAt: state.otpSentAt,
        otpStep: state.otpStep,
        formData: {
          ...state.formData,
          password: '', // Never persist password
        },
      }),
      version: 6, // Bump: added Google mode fields
      migrate: () => {
        // Return fresh state on version mismatch (clears stale data)
        return {
          currentStep: 0,
          intent: null,
          qualificationData: { ...initialQualificationData },
          isGoogleMode: false,
          googleCredential: null,
          isLineMode: false,
          lineUserId: null,
          linePictureUrl: null,
          phoneVerified: false,
          otpRefCode: null,
          otpSentAt: null,
          otpStep: 'phone' as const,
          formData: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
            companyName: '',
            phoneNumber: '',
            businessType: null,
            appInterests: [],
            realEmail: '',
          },
        };
      },
      onRehydrateStorage: () => () => {
        // Mark as hydrated after rehydration completes
        useRegistrationStore.setState({ hasHydrated: true });
      },
    }
  )
);

// Trigger hydration on module load (matches authStore pattern)
if (typeof window !== 'undefined') {
  useRegistrationStore.persist.rehydrate();
}
